import { supabase } from '../../lib/supabaseClient';
import {
  parseTaskFieldFlags,
  type TaskFieldFlags,
} from '../../lib/tasks/taskFields';
import {
  parseDossierFieldFlags,
  type DossierFieldFlags,
} from '../../lib/dossiers/dossierFields';
import {
  parseDossierRegistrySettings,
  type DossierRegistrySettings,
} from '../../lib/dossiers/registrySettings';
import { parseDateFormat, type DateFormatStyle } from '../../lib/dateFormat';
import { parseTaskQuickCapture } from '../../lib/tasks/taskQuickCapture';
import {
  assertAtLeastOneTaskView,
  parseTaskViewFlags,
  type TaskViewFlags,
} from '../../lib/tasks/taskViews';
import type { Database, Enums } from '../../types/database.types';

export type DossierPermissionScope = Enums<'dossier_permission_scope'>;

export type CabinetSettings = {
  dossierPermissionScope: DossierPermissionScope;
  taskViews: TaskViewFlags;
  taskFields: TaskFieldFlags;
  taskQuickCapture: boolean;
  /** Aperçu au survol d'une tâche. Défaut true. */
  taskHoverPreview: boolean;
  dossierFields: DossierFieldFlags;
  dossierRegistry: DossierRegistrySettings;
  dateFormat: DateFormatStyle;
};

type CabinetSettingsRow = {
  dossier_permission_scope?: unknown;
  task_view_list?: unknown;
  task_view_board?: unknown;
  task_view_calendar?: unknown;
  task_field_tags?: unknown;
  task_field_priority?: unknown;
  task_field_sections?: unknown;
  task_quick_capture?: unknown;
  task_hover_preview?: unknown;
  dossier_prescription_enabled?: unknown;
  dossier_legal_aid_enabled?: unknown;
  dossier_view_board?: unknown;
  dossier_registry_filters?: unknown;
  date_format?: unknown;
};

export function parseDossierPermissionScope(value: unknown): DossierPermissionScope {
  return value === 'per_dossier' ? 'per_dossier' : 'global';
}

export function parseCabinetSettings(
  row: CabinetSettingsRow | null | undefined,
): CabinetSettings {
  return {
    dossierPermissionScope: parseDossierPermissionScope(row?.dossier_permission_scope),
    taskViews: parseTaskViewFlags({
      list: typeof row?.task_view_list === 'boolean' ? row.task_view_list : undefined,
      board: typeof row?.task_view_board === 'boolean' ? row.task_view_board : undefined,
      calendar:
        typeof row?.task_view_calendar === 'boolean' ? row.task_view_calendar : undefined,
    }),
    taskFields: parseTaskFieldFlags({
      tags: typeof row?.task_field_tags === 'boolean' ? row.task_field_tags : undefined,
      priority:
        typeof row?.task_field_priority === 'boolean' ? row.task_field_priority : undefined,
      sections:
        typeof row?.task_field_sections === 'boolean' ? row.task_field_sections : undefined,
    }),
    taskQuickCapture: parseTaskQuickCapture(row?.task_quick_capture),
    taskHoverPreview: row?.task_hover_preview !== false,
    dossierFields: parseDossierFieldFlags({
      prescription: row?.dossier_prescription_enabled,
      legalAid: row?.dossier_legal_aid_enabled,
    }),
    dossierRegistry: parseDossierRegistrySettings({
      board: row?.dossier_view_board,
      filters: row?.dossier_registry_filters,
    }),
    dateFormat: parseDateFormat(row?.date_format),
  };
}

const SETTINGS_SELECT =
  'dossier_permission_scope, task_view_list, task_view_board, task_view_calendar, task_field_tags, task_field_priority, task_field_sections, task_quick_capture, task_hover_preview, dossier_prescription_enabled, dossier_legal_aid_enabled, dossier_view_board, dossier_registry_filters, date_format';

export async function getCabinetSettings(): Promise<CabinetSettings> {
  const { data, error } = await supabase
    .from('cabinet_settings')
    .select(SETTINGS_SELECT)
    .eq('id', 1)
    .maybeSingle();
  if (error) throw new Error(`Failed to read cabinet settings: ${error.message}`);
  return parseCabinetSettings(data);
}

export async function getDossierPermissionScope(): Promise<DossierPermissionScope> {
  return (await getCabinetSettings()).dossierPermissionScope;
}

export async function updateDossierPermissionScope(
  scope: DossierPermissionScope,
  updatedBy?: string | null,
): Promise<DossierPermissionScope> {
  const patch: Database['public']['Tables']['cabinet_settings']['Update'] = {
    dossier_permission_scope: scope,
    updated_by: updatedBy ?? null,
  };
  const { data, error } = await supabase
    .from('cabinet_settings')
    .update(patch)
    .eq('id', 1)
    .select('dossier_permission_scope')
    .single();
  if (error) throw new Error(`Failed to update cabinet settings: ${error.message}`);
  return parseDossierPermissionScope(data?.dossier_permission_scope);
}

export async function updateTaskViewFlags(
  flags: TaskViewFlags,
  updatedBy?: string | null,
): Promise<TaskViewFlags> {
  assertAtLeastOneTaskView(flags);
  const patch: Database['public']['Tables']['cabinet_settings']['Update'] = {
    task_view_list: flags.list,
    task_view_board: flags.board,
    task_view_calendar: flags.calendar,
    updated_by: updatedBy ?? null,
  };
  const { data, error } = await supabase
    .from('cabinet_settings')
    .update(patch)
    .eq('id', 1)
    .select('task_view_list, task_view_board, task_view_calendar')
    .single();
  if (error) throw new Error(`Failed to update cabinet settings: ${error.message}`);
  return parseCabinetSettings(data).taskViews;
}

export async function updateTaskFieldFlags(
  flags: TaskFieldFlags,
  updatedBy?: string | null,
): Promise<TaskFieldFlags> {
  const patch: Database['public']['Tables']['cabinet_settings']['Update'] = {
    task_field_tags: flags.tags,
    task_field_priority: flags.priority,
    task_field_sections: flags.sections,
    updated_by: updatedBy ?? null,
  };
  const { data, error } = await supabase
    .from('cabinet_settings')
    .update(patch)
    .eq('id', 1)
    .select('task_field_tags, task_field_priority, task_field_sections')
    .single();
  if (error) throw new Error(`Failed to update cabinet settings: ${error.message}`);
  return parseCabinetSettings(data).taskFields;
}

export async function updateTaskQuickCapture(
  enabled: boolean,
  updatedBy?: string | null,
): Promise<boolean> {
  const patch: Database['public']['Tables']['cabinet_settings']['Update'] = {
    task_quick_capture: enabled,
    updated_by: updatedBy ?? null,
  };
  const { data, error } = await supabase
    .from('cabinet_settings')
    .update(patch)
    .eq('id', 1)
    .select('task_quick_capture')
    .single();
  if (error) throw new Error(`Failed to update cabinet settings: ${error.message}`);
  return parseTaskQuickCapture(data?.task_quick_capture);
}

export async function updateTaskHoverPreview(
  enabled: boolean,
  updatedBy?: string | null,
): Promise<boolean> {
  const patch: Database['public']['Tables']['cabinet_settings']['Update'] = {
    task_hover_preview: enabled,
    updated_by: updatedBy ?? null,
  };
  const { data, error } = await supabase
    .from('cabinet_settings')
    .update(patch)
    .eq('id', 1)
    .select('task_hover_preview')
    .single();
  if (error) throw new Error(`Failed to update cabinet settings: ${error.message}`);
  return data?.task_hover_preview !== false;
}

export async function updateDossierFieldFlags(
  flags: DossierFieldFlags,
  updatedBy?: string | null,
): Promise<DossierFieldFlags> {
  const patch: Database['public']['Tables']['cabinet_settings']['Update'] = {
    dossier_prescription_enabled: flags.prescription,
    dossier_legal_aid_enabled: flags.legalAid,
    updated_by: updatedBy ?? null,
  };
  const { data, error } = await supabase
    .from('cabinet_settings')
    .update(patch)
    .eq('id', 1)
    .select('dossier_prescription_enabled, dossier_legal_aid_enabled')
    .single();
  if (error) throw new Error(`Failed to update cabinet settings: ${error.message}`);
  return parseCabinetSettings(data).dossierFields;
}

export async function updateDossierRegistrySettings(
  settings: DossierRegistrySettings,
  updatedBy?: string | null,
): Promise<DossierRegistrySettings> {
  const patch: Database['public']['Tables']['cabinet_settings']['Update'] = {
    dossier_view_board: settings.board,
    dossier_registry_filters: settings.filters,
    updated_by: updatedBy ?? null,
  };
  const { data, error } = await supabase
    .from('cabinet_settings')
    .update(patch)
    .eq('id', 1)
    .select('dossier_view_board, dossier_registry_filters')
    .single();
  if (error) throw new Error(`Failed to update cabinet settings: ${error.message}`);
  return parseCabinetSettings(data).dossierRegistry;
}

export async function updateDateFormat(
  style: DateFormatStyle,
  updatedBy?: string | null,
): Promise<DateFormatStyle> {
  const patch: Database['public']['Tables']['cabinet_settings']['Update'] = {
    date_format: style,
    updated_by: updatedBy ?? null,
  };
  const { data, error } = await supabase
    .from('cabinet_settings')
    .update(patch)
    .eq('id', 1)
    .select('date_format')
    .single();
  if (error) throw new Error(`Failed to update cabinet settings: ${error.message}`);
  return parseDateFormat(data?.date_format);
}
